import React from 'react';
import styled from 'styled-components';
import { useFormatter, useTranslations } from 'use-intl';
import { Calendar } from 'assets/svgs';

// Intl has no stable abbreviation for many zones (Seoul renders as "GMT+9"
// in most locales), so the schedule is shown as a zoned time followed by the
// IANA zone name it was entered in — unambiguous in every language.
export const SCHEDULE_DATE_TIME_FORMAT = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
};

function ScheduleLine({ question, onSchedule, onClear }) {
  const t = useTranslations('Review');
  const tCommon = useTranslations('Common');
  const format = useFormatter();
  const scheduledAt = question.scheduledVisibleAt?.toDate?.();

  if (!scheduledAt) {
    return (
      <Line>
        <LinkButton type="button" onClick={onSchedule}>
          <Calendar aria-hidden="true" />
          {t('scheduleReveal')}
        </LinkButton>
      </Line>
    );
  }

  const zone = question.scheduledVisibleTimezone;
  const dateTime = format.dateTime(scheduledAt, {
    ...SCHEDULE_DATE_TIME_FORMAT,
    timeZone: zone
  });

  return (
    <Line>
      <Calendar aria-hidden="true" />
      <span>
        {t('scheduledVisibleFrom', { dateTime })}
        {zone && ` · ${zone}`}
        {question.notifyOnReveal === true && ` · ${t('scheduleNotifyBadge')}`}
      </span>
      <LinkButton type="button" onClick={onSchedule}>
        {tCommon('edit')}
      </LinkButton>
      <LinkButton type="button" onClick={onClear}>
        {t('scheduleClear')}
      </LinkButton>
    </Line>
  );
}

const Line = styled.div`
  align-items: center;
  color: var(--blue);
  display: flex;
  flex-wrap: wrap;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  gap: 0.5rem 0.75rem;
  line-height: 120%;
  margin-top: 1.1rem;

  svg {
    flex-shrink: 0;
    height: 1rem;
    width: 1rem;

    g {
      stroke: currentColor;
    }
  }
`;

const LinkButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: var(--blue);
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;
  gap: 0.4rem;
  line-height: inherit;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 0.15em;

  &:hover,
  &:focus-visible {
    color: var(--dark-blue);
    outline: none;
  }
`;

export default ScheduleLine;
